'use client';

import { c as _c } from "react/compiler-runtime";
import React, { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type * as nuqs from 'nuqs';
import { useQueryStates, useQueryState, parseAsInteger } from 'nuqs';
import { usePathname } from 'next/navigation';
import type { UIMessage } from 'ai';
import { Button, Icon, type IconName, InputSelect, Toggle } from '~/components/primitives';
import { X } from 'lucide-react';
import { useActivitySearchHeroBarInViewStore } from '../activity-search-hero-bar-in-view-store';
import { getActiveFilterCount } from './sidebar-utils';
import { useI18n } from '~/features/i18n';
import { backgroundColorVariant } from '~/utils/variants';
import type { VariantProps } from 'class-variance-authority';
import { usePortalCtx } from '~/features/global/portal';
import { useVATInclPreference, VATKindDisplay } from '~/features/portals/money';
import { DEFAULT_DISPLAY_PARTICIPANTS } from '@funkey/constants';
import { usePlpExperiment } from '../plpExperiment';
import { usePlpUsps } from './plpUsps';
import { PlpPromotionCard } from './PlpPromotionCard';
import { useActivityFilterDrawerStore } from '../activity-filter-drawer-store';
import { mapUnion, std } from '@funkey/shared';
import Title from '~/components/utils/Title';
import { useRegionsQuery, useLanguagesQuery } from '~/features/activities/ActivityFilterBar/config/shared';
import type { ClassValue } from 'class-variance-authority/types';
import { useLocalStorageState } from '~/modules/browser/storage';
import { keepPreviousData, useQuery, useInfiniteQuery, type UseInfiniteQueryResult, type InfiniteData } from '@tanstack/react-query';
import { useThrottledValue, useDebouncedCallback } from '~/components/hooks/throttling';
import throttle from 'lodash/throttle';
import * as motion from 'motion/react-client';
import { AnimatePresence, stagger } from 'motion/react';
import { usePostHog } from 'posthog-js/react';
import type { PostHog_FeaturesSDK } from '~/modules/posthog/features';
import { useTRPC, useTRPCClient } from '~/modules/trpc/client';
import type { TRPC_ROUTER } from '~/modules/trpc/routers';
import { cn } from '~/utils';
import type { ClassNameValue } from 'tailwind-merge';
import { PostHog_ClientFeaturesSDK } from '~/modules/posthog/features/client';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { Auth } from '~/modules/auth/client';
import type { PartnerReferralPage } from '@funkey/types/graphql/graphql';
import { Action_RestoreScroll } from '~/modules/browser/scroll';
import MobileSortSlideUp from './MobileSortSlideUp';
import { ActivityFiltersLayout } from './ActivityFilterBar';
import { useSubCategoryRow } from '../useSubCategoryRow';
import { buildActiveFilterPills } from './activeFilterPills';
import { isNearPointActive, NEAR_POINT_CLEARED, useHasPrefilledNearPoint } from './nearPoint';
import ACTIVITY_FILTER_BAR_PRESET from './ActivityFilterBar/presets/search.activityFilterPreset';
import { CACHED_PARTICIPANTS_FILTER, CACHED_REGION_CITY_SEARCH, useActivitySearchCtx } from './context';
import { ActivityCard } from '../../ActivityCard';
import { ActivityCard_Skeleton } from '../../ActivityCard/ActivtiyCardSkeleton';
import { ActivityAiMobileCtaBar } from './ActivityAiMobileCtaBar';
import { ActivityAiMobileLanding } from './ActivityAiMobileLanding';
import { ActivityAiMobileLandingComposer } from './ActivityAiMobileLandingComposer';
import { ActivityAiSearchFilterPanel } from '../ActivityAiSearchFilterPanel';
import { ActivityCardGrid_NotFound } from '../../ActivityCardGrid';
import { useActivityAiActiveChat } from '../useActivityAiActiveChat';
import { useAiChatHistorySync } from '../useAiChatHistory';
import { ActivityFilterDrawerButton, ActivityFilterDrawerOverlay, ActivityFiltersSidebar } from './ActivityFilterDrawer';
import { useActivitySearchMode, useAiSearchEnabled } from '../useActivitySearchMode';
import { SearchModeToggle } from '../SearchModeToggle';
import { useResultMessageIdParam } from './ActivityAiResultsBridge';
import { useSelectedSearchTurn, ActivityAi_SearchTurn, type AiActivityScore, type AiActivityEntry, type AiTurnFilters } from '../activity-ai-search-turn';
import type { ActivitySearchPage } from '../server';
import { ACTIVITY_SORT_QUERY_PARAMS, _ACTIVITY_SEARCH_QUERY_OPTIONS, ACTIVITY_BOOST_AD_QUERY_PARAMS, ActivityFilterResults_calculatePagination, resolveInjectedCard, type FacetCounts } from '../shared';
import { buildShowMorePrompt, setFollowup } from '../activity-ai-chat-runtime-store';
import { useIsTailwindBreakpoint } from '~/modules/tailwind/hooks/useIsTailwindBreakpoint';
import { shouldFetchNextPage } from './shouldFetchNextPage';
import { Toaster } from '~/components/ui/sonner';

/** Stable empty reference so `useSelectedSearchTurn` doesn't re-derive on every non-AI render. */
const EMPTY_MESSAGES: ReadonlyArray<UIMessage> = [];

/** Stable empty AI-entry reference so query inputs don't churn on non-AI renders. */
const EMPTY_AI_ENTRIES: AiActivityEntry[] = [];

/**
 * The `getAiResults` query MUST be parameterized by the CURRENT turn's structured
 * filters (derived synchronously from the same message the entries come from), NOT
 * by the throttled URL — the URL only catches up via a gated side-channel, so the
 * grid would fire budget-less and surface over-budget group representatives
 * (FUN-4558). Overriding budget/participants/sort/locationType with the turn's
 * values (including its nulls — a turn that dropped a constraint clears it) makes
 * the turn authoritative for exactly these fields while everything else still
 * comes from the URL echo. `maxBudget` stays the displayed VAT-incl value; the
 * server converts incl→excl, so it is passed through unchanged.
 */
function withAiTurnFilterParams<T extends Record<string, unknown>>(params: T, turnFilters: AiTurnFilters): T & Pick<AiTurnFilters, 'maxBudget' | 'minBudget' | 'participants' | 'sort' | 'locationType'> {
  return {
    ...params,
    maxBudget: turnFilters.maxBudget,
    minBudget: turnFilters.minBudget,
    participants: turnFilters.participants,
    sort: turnFilters.sort,
    locationType: turnFilters.locationType
  };
}
function scrollResultsIntoView(element: HTMLElement) {
  requestAnimationFrame(() => {
    element.scrollIntoView({
      block: 'start',
      behavior: 'smooth'
    });
  });
}
function useScrollToResultsOnChange(ref, signature, t0) {
  const $ = _c(5);
  const enabled = t0 === undefined ? true : t0;
  const previousSignatureRef = useRef(signature);
  const wasEnabledRef = useRef(enabled);
  const absorbSignatureChurnRemainingRef = useRef(0);
  let t1;
  let t2;
  if ($[0] !== enabled || $[1] !== ref || $[2] !== signature) {
    t1 = () => {
      if (!enabled) {
        wasEnabledRef.current = false;
        return;
      }
      if (!wasEnabledRef.current) {
        wasEnabledRef.current = true;
        previousSignatureRef.current = signature;
        absorbSignatureChurnRemainingRef.current = 2;
        return;
      }
      if (absorbSignatureChurnRemainingRef.current > 0) {
        absorbSignatureChurnRemainingRef.current = absorbSignatureChurnRemainingRef.current - 1;
        previousSignatureRef.current = signature;
        return;
      }
      if (previousSignatureRef.current === signature) {
        return;
      }
      previousSignatureRef.current = signature;
      if (!ref.current) {
        return;
      }
      scrollResultsIntoView(ref.current);
    };
    t2 = [enabled, ref, signature];
    $[0] = enabled;
    $[1] = ref;
    $[2] = signature;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
}

/** @TODO: move this to a posthog specific module */
function getFeatureFlag<T extends PostHog_FeaturesSDK.Name>(posthog: ReturnType<typeof usePostHog>, name: T, defaultValue: PostHog_FeaturesSDK.Variant<T>): PostHog_FeaturesSDK.Variant<T> {
  const value = posthog.getFeatureFlag(name) as PostHog_FeaturesSDK.Variant<T> | undefined;
  return value || defaultValue;
}
interface ActivityFilterResultsProps {
  referralPageSlug?: PartnerReferralPage['slug'];
  className?: string;
  backgroundColor?: VariantProps<typeof backgroundColorVariant>['backgroundColor'];
  anchorId?: string;
  categoryID?: number;
  showEnginePill?: boolean;
  engineName?: string;
  /** Extra info that this component needs to work properly with SSR
   *
   * Set it to null if this component doesn't need to be SSR'ed */
  ssrCtx?: {
    weightSet: PostHog_FeaturesSDK.Variant<'alternate-elastic-weights'>;
  } | null;
  customization?: ActivitySearchPage.SearchCustomizationProps;
  cardVariant?: PostHog_FeaturesSDK.Variant<'plp-cards'>;
  weightSet?: PostHog_FeaturesSDK.Variant<'alternate-elastic-weights'>;
  filterLayoutVariant?: PostHog_FeaturesSDK.Variant<'plp-filter-sidebar'>;
}

/** PostHog dimensions describing the active location/distance filters, mixed
 * into `plp.view` so distance-search usage is measurable (adoption, radii,
 * route preference, distance sort). */
function locationTrackingProps(filterValues: useActivitySearchCtx.FilterValues) {
  return {
    location_near_active: isNearPointActive(filterValues),
    location_near_label: filterValues.nearLabel ?? null,
    location_radius_km: filterValues.radius ?? null,
    location_routes: filterValues.location,
    location_regions: filterValues.regions ?? [],
    location_sorted_by_distance: filterValues.sort === 'distance.ASC'
  };
}
export const ActivityFilterResults: React.FC<ActivityFilterResultsProps> = props => {
  const $ = _c(10);
  const posthog = usePostHog();
  useAiChatHistorySync();
  let t0;
  if ($[0] !== posthog || $[1] !== props.filterLayoutVariant) {
    t0 = props.filterLayoutVariant ?? PostHog_ClientFeaturesSDK.getFlag(posthog, "plp-filter-sidebar", "control") ?? "control";
    $[0] = posthog;
    $[1] = props.filterLayoutVariant;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const filterLayoutVariant = t0;
  let t1;
  if ($[3] !== filterLayoutVariant || $[4] !== props) {
    t1 = <ActivityFilterResult.InfiniteScroll {...props} filterLayoutVariant={filterLayoutVariant} />;
    $[3] = filterLayoutVariant;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ActivityFilterDrawerOverlay />;
    t3 = <Toaster position="bottom-center" richColors={true} />;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1) {
    t4 = <>{t1}{t2}{t3}</>;
    $[8] = t1;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const ActivityResultsLayout: React.FC<{
  /** Whether the desktop filter sidebar feature is enabled at all (drives layout container) */
  enableDesktopFilterSidebar: boolean;
  /** Sidebar variant: 'filters' (default, narrower) or 'ai' (wider track to fit chat composer + suggestions). */
  variant: 'filters' | 'ai';
  sidebar?: React.ReactNode;
  beforeContent?: React.ReactNode;
  children: React.ReactNode;
}> = props => {
  const $ = _c(18);
  const isFinetuning = usePlpExperiment();
  if (!props.enableDesktopFilterSidebar) {
    return props.children;
  }
  const isAi = props.variant === "ai";
  const t0 = isFinetuning && "pt-8";
  const t1 = isAi ? "[--sidebar-w:380px] xl:[--sidebar-w:408px]" : "[--sidebar-w:312px]";
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = cn("grid min-h-[calc(100dvh-4.5rem)] lg:grid-cols-[var(--sidebar-w)_minmax(0,1fr)]", t0, t1);
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== props.variant) {
    t3 = props.variant === "filters" ? <ActivityFilterBar_SubCategoryRow backgroundType="light" className="col-span-full min-w-0" /> : null;
    $[3] = props.variant;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = isAi ? "pr-4 xl:pr-6" : "pr-4";
  const t5 = isFinetuning && (isAi ? "lg:pt-2" : "lg:pt-4");
  let t6;
  if ($[5] !== t4 || $[6] !== t5) {
    t6 = cn("w-full", t4, t5);
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== props.sidebar || $[9] !== t6) {
    t7 = <aside className="hidden shrink-0 self-start lg:sticky lg:top-header-visible lg:block"><div className={t6}>{props.sidebar}</div></aside>;
    $[8] = props.sidebar;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== props.children) {
    t8 = <div className="min-w-0 lg:self-start">{props.children}</div>;
    $[11] = props.children;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t7 || $[16] !== t8) {
    t9 = <div className={t2}>{t3}{t7}{t8}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
};

/** Dummy activity grid while AI “search” placeholder runs (until wired to retrieval). */
const ActivityAiResultsPlaceholderSkeleton: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="@container"><div data-testid="ai-results-loading" className="@min-[500px]:grid-cols-2 @min-[700px]:grid-cols-3 @min-[1100px]:grid-cols-4 grid grid-cols-1 gap-4" aria-busy={true}>{Array.from({
          length: 8
        }, _temp)}</div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
namespace ActivityFilterResult {
  /** Shared header component for both pagination and infinite scroll */
  const Header: React.FC<{
    totalResults: number | undefined;
    isFetching?: boolean;
    showEnginePill?: boolean;
    engineName?: string;
    backgroundColor?: VariantProps<typeof backgroundColorVariant>['backgroundColor'];
    showDesktopFilterSidebar?: boolean;
    /** The group size the listed prices are calculated for. */
    displayParticipants: number;
    /** Results per filter option, counted on the list the page shows. */
    facetCounts?: FacetCounts;
    /** In AI mode the AI panel owns filtering + sort; hide the redundant header controls. */
    isAi?: boolean;
  }> = props => {
    const i18n = useI18n();
    const portal = usePortalCtx();
    const isFinetuning = usePlpExperiment();
    // The reworked layout keeps the functional part beige on every page, so a
    // Storyblok section with a dark background needs no separate colour rules.
    const background = isFinetuning ? 'brown-50' as const : props.backgroundColor;
    const setDrawerTotalResults = useActivityFilterDrawerStore(s => s.setTotalResults);
    const setFacetCounts = useActivityFilterDrawerStore(s_0 => s_0.setFacetCounts);
    const openFilterDrawer = useActivityFilterDrawerStore(s_1 => s_1.open);
    const headerCtx = useActivitySearchCtx();
    const headerFilters = headerCtx.useFilters();
    const activeFilterCount = getActiveFilterCount(headerFilters.value);
    // Once the hero filter bar scrolls under the site header, this sticky
    // header carries the same bar folded to one line; the popovers are shared.
    const heroBarInView = useActivitySearchHeroBarInViewStore(s_2 => s_2.heroBarInView);
    const showStickyBar = isFinetuning && !heroBarInView && !props.isAi;

    // The filter sheet's button says how many teambuildings it will show and
    // the sidebar shows a count per category; this header is the one place
    // that has both numbers.
    useEffect(() => {
      setDrawerTotalResults(props.isFetching ? null : props.totalResults ?? null);
    }, [props.totalResults, props.isFetching, setDrawerTotalResults]);
    // Counts per option belong to the reworked sidebar only. Cleared while a
    // search is in flight: the query keeps the previous page's data, and
    // counting the old result set withdraws options the new filters do offer.
    useEffect(() => {
      setFacetCounts(isFinetuning && !props.isFetching ? props.facetCounts ?? null : null);
    }, [props.facetCounts, props.isFetching, isFinetuning, setFacetCounts]);

    // The control page drops this header in AI mode, as it did before the
    // experiment: there the panel owns the count, the filters and the sort.
    // Keeping it would put a bar on the control group's AI page that the
    // experiment was never supposed to touch.
    if (props.isAi && !isFinetuning) return null;

    // AI mode keeps the count and the price basis: the chat answers in cards that
    // show a price, and this is the only place that says which group size those
    // prices assume. Filtering and sort do belong to the AI panel, so the controls
    // go, but the bar sticks the same as everywhere else.
    const showHeaderControls = !props.isAi;
    return (
      // A fragment, not a wrapper: a sticky element only sticks within its
      // parent, so the header has to be a direct child of the results column.
      <>
                <header className={cn('flex items-center justify-between gap-3',
        // A container so the sort control can fold to its icon on the
        // width it actually has, which the sidebar and the AI panel
        // change without the viewport moving.
        '@container/results-header',
        // On phones the AI composer already owns the bottom of the
        // screen and the panel scrolls behind this; a second fixed strip
        // eats the little room left, so it only sticks from desktop up.
        props.isAi ? 'lg:sticky lg:z-2' : 'sticky z-2',
        // The pills sit right under the price basis, so the row keeps
        // its top padding but hands the space below to them.
        isFinetuning && !props.isAi && 'mb-1 pb-2 pt-4',
        // AI mode has no controls on this row, so it is two lines of text
        // that should start level with the panel beside it and not hold
        // the cards away from them.
        isFinetuning && props.isAi && 'mb-3 flex-wrap pb-1 pt-2', !isFinetuning && 'mb-2 py-4 md:py-2',
        // The control page lets the sidebar carry the count on desktop;
        // the reworked page keeps count, price basis and sort above the
        // results on every screen.
        props.showDesktopFilterSidebar && !isFinetuning && 'lg:hidden', portal.portal === 'WEB' && (background ? backgroundColorVariant({
          backgroundColor: background
        }) : 'bg-brown-50'), portal.portal === 'WEB' && 'top-header-visible', portal.portal === 'BIZZ' && 'top-20 -mx-1 bg-brown-50 px-1', background === 'blue-950' ? 'text-white' : 'text-blue')}>
                    <motion.div className="flex min-w-0 flex-1 flex-col gap-0.5" animate={{
            opacity: props.isFetching ? 0.2 : 1
          }} transition={{
            duration: 0.2,
            ease: 'easeOut'
          }}>
                        {isFinetuning ? <Title as="h2" size="lg" weight="bold" className="text-base leading-tight" data-testid="activity-plp-results-count">
                                {i18n._('activity.plp.resultCount', {
                count: props.totalResults ?? '-'
              })}
                            </Title> : <span data-testid="activity-plp-results-count" className="text-sm font-medium sm:text-base">
                                {i18n._('{count} teambuildings found', {
                count: props.totalResults ?? '-'
              })}
                            </span>}
                        {/* The group size the prices are quoted at, stated once for the
                         page instead of on every card. Same value the server priced
                         with, so the cards' totals cannot contradict it. */}
                        {isFinetuning && <span data-testid="activity-plp-price-basis"
            // Truncates rather than wraps: on a phone the sort and
            // filter buttons share this line, and a second line of
            // text would push them under the header.
            className="truncate text-sm text-gray-500">
                                {i18n._('activity.plp.priceBasis', {
                count: props.displayParticipants
              })}{' '}
                                <VATKindDisplay hideParentheses />
                            </span>}
                    </motion.div>

                    {/*
                     Sort is always present so the header keeps a stable height.
                     Only the filter drawer button collapses in/out (in AI mode the AI
                     panel owns filtering, so the redundant filter button hides).
                     GPU-friendly: filter button uses `scaleX` + `opacity` (transform-only,
                     fully composited). `layout` on the menu uses Motion's FLIP technique —
                     measures before/after, animates with `transform` — so the sort button
                     slides via transform on the GPU rather than a `width` reflow.
                     */}
                    {/* Fades and lifts in rather than appearing outright: the sentinel that
                        drives it flips the moment the hero bar clears the site header, and a
                        bare mount read as a flicker on every small scroll near that line.
                        Opacity and transform only, so it stays on the compositor. */}
                    <AnimatePresence initial={false}>
                        {showStickyBar && <motion.div key="activity-results-header-sticky-bar"
            // Equal flex on the count, this bar and the sort menu
            // gives the row three columns of the same width, so the
            // pill sits in the middle of the results area rather
            // than wherever the count happens to end.
            className="hidden min-w-0 justify-center lg:flex lg:flex-1" initial={{
              opacity: 0,
              y: -6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.18,
              ease: [0.32, 0.72, 0, 1]
            }} style={{
              willChange: 'transform, opacity'
            }}>
                                <ActivityFilterBar variant="pill" backgroundType="light" dense hideModeToggle wrapperClassName="min-w-0" />
                            </motion.div>}
                    </AnimatePresence>
                    {isFinetuning && !props.isAi && <div className="flex shrink-0 items-center gap-2 lg:hidden">
                            <ActivitySortButton variant="icon" />
                            <button type="button" data-testid="plp-filter-drawer-mobile-button" onClick={() => openFilterDrawer()} className={cn('relative flex size-11 items-center justify-center rounded-full',
            // Green only while a filter is active, like the bar's icons.
            activeFilterCount > 0 ? 'bg-secondary text-white' : 'shadow-xs bg-white text-primary')}>
                                <Icon name="filters" size="sm" />
                                <span className="sr-only">{i18n._('Filters')}</span>
                                {activeFilterCount > 0 && <span data-testid="plp-filter-drawer-mobile-count" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-tertiary px-1 text-[11px] font-bold leading-none text-white">
                                        {activeFilterCount}
                                    </span>}
                            </button>
                        </div>}
                    <motion.menu layout={!isFinetuning} className={cn('flex items-center gap-3', isFinetuning && 'max-lg:hidden lg:flex-1 lg:justify-end', props.isAi && 'hidden')}>
                        <AnimatePresence initial={false}>
                            {showHeaderControls ? <motion.div layout={!isFinetuning} key="activity-results-header-filter-button" initial={{
                opacity: 0,
                scaleX: 0
              }} animate={{
                opacity: 1,
                scaleX: 1
              }} exit={{
                opacity: 0,
                scaleX: 0
              }} transition={{
                duration: 0.25,
                ease: [0.32, 0.72, 0, 1]
              }} style={{
                transformOrigin: 'right center',
                willChange: 'transform, opacity'
              }}>
                                    <ActivityFilterDrawerButton i18n={i18n} hideDesktopButton={props.showDesktopFilterSidebar} />
                                </motion.div> : null}
                        </AnimatePresence>
                        <ActivitySortButton variant={isFinetuning ? 'responsive' : 'full'} />
                    </motion.menu>
                </header>
                {isFinetuning && !props.isAi && <ActiveFilterPills backgroundType="light" placement="results-header" />}
            </>
    );
  };
  function deriveVATRate(portal: usePortalCtx.CtxType, vatPref: {
    enabled: boolean;
    rate: number;
  }): number | null {
    if (portal.portal === 'BIZZ' && portal.bizz.vatInclusive) return portal.bizz.vatRate;
    if (portal.portal === 'WEB' && vatPref.enabled) return vatPref.rate;
    return null;
  }
  export const InfiniteScroll: React.FC<ActivityFilterResultsProps> = props => {
    const i18n = useI18n();
    const posthog = usePostHog();
    const portal = usePortalCtx();
    const vatPref = useVATInclPreference();
    const trpcClient = useTRPCClient();
    const ctx = useActivitySearchCtx();
    const filters = ctx.useFilters();
    const [BOOST_AD_QP] = useQueryStates(ACTIVITY_BOOST_AD_QUERY_PARAMS);
    const customization = props.customization ?? ctx.customization;
    const showDesktopFilterSidebar = props.filterLayoutVariant === 'sidebar';
    const {
      isAi
    } = useActivitySearchMode();
    const aiActiveChat = useActivityAiActiveChat();
    const showMobileAiLanding = isAi && aiActiveChat.messages.length === 0;
    const cardVariant = getFeatureFlag(posthog, 'plp-cards', props.cardVariant ?? 'control');
    const weightSet = props.ssrCtx?.weightSet ?? props.weightSet ?? getFeatureFlag(posthog, 'alternate-elastic-weights', 'control');
    const vatRate = deriveVATRate(portal, vatPref);

    // `resultMessageId` pins the derivation to a past turn ("go back" nav from
    // the chat panel); `null`/absent defaults to the latest turn with results.
    const [resultMessageId] = useResultMessageIdParam();
    const searchTurn = useSelectedSearchTurn(isAi ? aiActiveChat.uiMessages : EMPTY_MESSAGES, resultMessageId);
    const aiEntries = ActivityAi_SearchTurn.$is('Cached')(searchTurn) ? searchTurn.entries : EMPTY_AI_ENTRIES;
    const aiTurnFilters = ActivityAi_SearchTurn.$is('Cached')(searchTurn) ? searchTurn.filters : null;
    const aiActivityIds = ActivityAi_SearchTurn.$is('Cached')(searchTurn) ? searchTurn.ids : [];

    // AI-in-flight loading state (FUN-4558): the chat is extracting but the
    // pgvector id set hasn't arrived yet, so the grid would otherwise fall
    // back to the full popular/ES set and look like a finished answer. Show
    // the standard skeleton grid (`ActivityAiResultsPlaceholderSkeleton`)
    // instead until the results arrive.
    const isAiLoading = isAi && (aiActiveChat.status === 'submitted' || aiActiveChat.status === 'streaming') && aiActivityIds.length === 0;
    // Admin-only AI score badge on the cards. Scores ride the SAME
    // `search_activities` tool part the ids come from; non-admins never read it.
    const isSuperAdmin = Auth.useSession('optional').user?.isSuperAdmin === true;
    const adminScores = isSuperAdmin && ActivityAi_SearchTurn.$is('Cached')(searchTurn) ? searchTurn.scores : undefined;
    const throttledFilters = useThrottledValue(filters.value, 1000, [filters.value]);
    const isFinetuning = usePlpExperiment();
    const trpc = useTRPC();
    const cityCache = useLocalStorageState(CACHED_REGION_CITY_SEARCH);
    const cachedCity = cityCache.isMounted ? cityCache.state : null;
    const prioritizeRegionsQuery = useQuery(trpc.regions.searchClientRegionsWithinRadius.queryOptions(cachedCity ? {
      coords: {
        latitude: cachedCity.lat,
        longitude: cachedCity.lng
      },
      radiusKm: cachedCity.radiusKm,
      countryCode: i18n.country,
      language: i18n.language
    } : undefined as never, {
      enabled: !!cachedCity
    }));
    const prioritizeRegionIDs = useMemo(() => prioritizeRegionsQuery.data?.map(r => Number(r.id)), [prioritizeRegionsQuery.data]);
    const queryOptions = useMemo(() => ({
      locale: i18n.locale,
      bizzID: portal.portal === 'BIZZ' ? portal.bizz.id : null,
      vatRate,
      constraints: customization?.constraints,
      cardVariant,
      weightSet,
      filters: throttledFilters,
      groupIDs: customization?.groupIDs,
      prioritizeRegionIDs,
      // In AI mode the grid renders the cached group entries; hydration
      // resolves each group's primary representative live (ADR 0007).
      aiEntries: isAi ? aiEntries : EMPTY_AI_ENTRIES,
      // Current turn's derived filters drive the AI query synchronously
      // (FUN-4558) — part of the key so a turn's budget/sort change refetches.
      aiTurnFilters: isAi ? aiTurnFilters : null,
      // A frozen id set hard-filters the grid, so injected ads from
      // outside that set would be off-topic. A filters turn (empty id set)
      // runs the classic search and keeps ads, identical to non-AI mode.
      includeAds: isAi && aiEntries.length > 0 ? false : customization?.includeAds ?? false,
      // Only the reworked filters render counts, so only they ask for
      // them. Part of the key, and the same value the server prefetched
      // with, or the grid refetches on mount.
      withFacetCounts: isFinetuning
    }), [aiEntries, aiTurnFilters, cardVariant, customization, i18n.locale, isAi, isFinetuning, portal, throttledFilters, prioritizeRegionIDs, vatRate, weightSet]);
    const infiniteQueryResult = useInfiniteQuery({
      queryKey: ['activities', 'search', 'virtualized-infinite', queryOptions],
      queryFn: async queryFnParams => {
        const {
          pageParam
        } = queryFnParams;
        const pagination = ActivityFilterResults_calculatePagination(portal.portal, pageParam);
        const params = {
          ...BOOST_AD_QP,
          ...queryOptions.filters,
          groupIDs: queryOptions.groupIDs,
          prioritizeRegionIDs: queryOptions.prioritizeRegionIDs,
          pagination
        };
        // AI mode: resolve each ranked group to its primary representative
        // live (ADR 0007), respecting the current filters, ordered by the
        // AI's relevance. The whole set fits one page (reps ≤ #entries), so
        // hydrate it in one shot and let the virtualizer slice it. The turn's
        // derived filters (budget/sort/participants) are authoritative here
        // (FUN-4558) so the server budget recheck fires without the throttled URL.
        if (queryOptions.aiEntries.length > 0 && queryOptions.aiTurnFilters) {
          return trpcClient.activities.getAiResults.query({
            ..._ACTIVITY_SEARCH_QUERY_OPTIONS({
              locale: queryOptions.locale,
              bizzID: queryOptions.bizzID,
              vatRate: queryOptions.vatRate,
              constraints: queryOptions.constraints,
              weightSet: queryOptions.weightSet,
              includeAds: queryOptions.includeAds,
              withFacetCounts: queryOptions.withFacetCounts
            }, withAiTurnFilterParams(params, queryOptions.aiTurnFilters)),
            entries: queryOptions.aiEntries,
            pagination: {
              pageIdx: 0,
              size: Math.max(queryOptions.aiEntries.length, 1)
            }
          });
        }
        return trpcClient.activities.search.query(_ACTIVITY_SEARCH_QUERY_OPTIONS({
          locale: queryOptions.locale,
          bizzID: queryOptions.bizzID,
          vatRate: queryOptions.vatRate,
          constraints: queryOptions.constraints,
          weightSet: queryOptions.weightSet,
          includeAds: queryOptions.includeAds,
          withFacetCounts: queryOptions.withFacetCounts
        }, params));
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        if (lastPageParam + 1 >= lastPage.pageInfo.totalPages) return undefined;
        return lastPageParam + 1;
      },
      getPreviousPageParam: (_firstPage, _allPages_0, firstPageParam) => {
        if (firstPageParam <= 0) return undefined;
        return firstPageParam - 1;
      },
      staleTime: Infinity,
      placeholderData: keepPreviousData
    });

    // AI mode returns representatives already in the AI's relevance order
    // (server-side, by group rank — ADR 0007); non-AI returns App Search order.
    // Either way the flattened pages are already correctly ordered.
    const allActivities = useMemo(() => infiniteQueryResult.data?.pages.flatMap(page => page.activities) ?? [], [infiniteQueryResult.data]);
    const totalResults = infiniteQueryResult.data?.pages[0]?.pageInfo.totalResults;

    // Routed through the followup store so it queues if a stream is in flight.
    const handleShowMore = useCallback(() => {
      setFollowup(buildShowMorePrompt(i18n._, allActivities.length));
    }, [allActivities.length, i18n]);
    const stableHeightRef = useStableHeight(infiniteQueryResult.isFetching);

    // Track logged activity IDs to avoid duplicate PostHog events
    const loggedActivityIdsRef = useRef<Set<number>>(new Set());

    // Track PLP view with ad impressions - only for new activities
    // Skip tracking when debug mode is active to avoid polluting analytics with fake traffic
    const isDebugMode = BOOST_AD_QP.debugAdDate !== null || BOOST_AD_QP.debugAdsOnly;
    useEffect(() => {
      if (!infiniteQueryResult.data) return;
      if (isDebugMode) return;
      const currentActivities = infiniteQueryResult.data.pages.flatMap(page_0 => page_0.activities) ?? [];
      if (!currentActivities.length) return;
      const allActivityIds = currentActivities.map(a => a.id);
      const newIds = allActivityIds.filter(id => !loggedActivityIdsRef.current.has(id));
      if (newIds.length > 0) {
        const newIdsSet = new Set(newIds);
        const newActivities = currentActivities.filter(a_0 => newIdsSet.has(a_0.id));
        const boostAdIds = newActivities.flatMap(activity => typeof activity.boostedAdID === 'number' ? [activity.boostedAdID] : []);
        posthog.capture('plp.view', {
          ...ctx.posthogEventCtx['plp.view'],
          activity_ids: newIds,
          group_ids: newActivities.map(a_1 => a_1.groupId),
          boost_ad_ids: boostAdIds,
          ...locationTrackingProps(filters.value)
        });
        newIds.forEach(id_0 => loggedActivityIdsRef.current.add(id_0));
      }
    }, [infiniteQueryResult.data, isDebugMode]);

    // Reset logged IDs when filters change (new search)
    useEffect(() => {
      loggedActivityIdsRef.current.clear();
    }, [queryOptions]);
    const resultsRef = useRef<HTMLDivElement>(null);

    // The PDP opens as an intercepted-route modal on mobile, which changes
    // the browser URL to the PDP path. This component stays mounted, so
    // its URL-reading hooks would treat the route change as a filter
    // change and trigger scroll-to-results. Freeze until pathname returns.
    // Using filters.value (URL-synced) rather than throttledFilters keeps
    // signature in lockstep with isOnPLPRoute, so no transient render
    // mid-throttle-catchup re-triggers a scroll after the modal closes.
    const pathname = usePathname();
    const initialPathnameRef = useRef(pathname);
    const isOnPLPRoute = pathname === initialPathnameRef.current;
    const minParticipants = filters.value.participants ?? null;
    useEffect(() => {
      if (!minParticipants) return;
      CACHED_PARTICIPANTS_FILTER.setValue(minParticipants);
    }, [minParticipants]);

    // Desktop only — see note above: on mobile the auto scroll-to-results
    // makes the page jump down while the user is still typing a filter.
    const isDesktop = useIsTailwindBreakpoint('lg');
    // Skip in AI mode: see note on the other results component — the grid is
    // chat-driven, so a filter-change scroll (e.g. new chat) yanks the page down.
    // The reworked layout keeps the functional part beige on every page, so a
    // Storyblok section with a dark background needs no separate colour rules.
    const background = isFinetuning ? 'brown-50' as const : props.backgroundColor;
    // Scrolling to the results only helps someone filtering from the hero bar at the
    // top of the page. Past it the visitor is already at the results and is using the
    // sticky summary bar, which this scroll would push back out of view mid-edit,
    // taking the open popover with it.
    const heroBarInView = useActivitySearchHeroBarInViewStore(state => state.heroBarInView);
    useScrollToResultsOnChange(resultsRef, JSON.stringify([std.Object.omit(filters.value, isFinetuning ? ['categories', 'subCategories', 'query'] : ['categories', 'subCategories']), BOOST_AD_QP]),
    // Past the hero bar the visitor is already at the results and is
    // editing through the sticky summary bar, which this scroll would
    // push out from under them. That bar is the reworked page's, so the
    // control page scrolls the way it always did.
    isOnPLPRoute && isDesktop && !isAi && (!isFinetuning || heroBarInView));
    return <motion.div ref={resultsRef} className={cn(!isAi && props.className,
    // The USP bar slides away on scroll, so what the header
    // still covers is a variable, not a fixed pair of steps.
    !isAi && (isFinetuning ? 'scroll-mt-[var(--funkey-header-visible-height)]' : 'scroll-mt-24 md:scroll-mt-28'), backgroundColorVariant({
      backgroundColor: background
    }),
    // City and region pages keep a dark hero above this, so the beige
    // used to start against it with a hard edge. Rounding the top
    // makes the functional part read as a sheet laid over the hero
    // instead of a colour that stops mid-page.
    isFinetuning && props.backgroundColor === 'blue-950' && 'rounded-t-4xl lg:rounded-t-5xl')}>
                {/* Scroll restoration is for PDP→PLP back-nav. In AI mode results
                    are ephemeral (id set from sessionStorage) and the `page` param
                    tracks the live scroll position, so restoring a stale offset
                    yanks the page to the footer on deep-link/reload — skip it. */}
                {!isAi ? <Suspense fallback={null}>
                        <Action_RestoreScroll />
                    </Suspense> : null}

                <ActivityResultsLayout enableDesktopFilterSidebar={showDesktopFilterSidebar} variant={isAi ? 'ai' : 'filters'} sidebar={showDesktopFilterSidebar ? <ActivityFiltersSidebar totalResults={totalResults} isFetching={infiniteQueryResult.isFetching} dark={background === 'blue-950'} /> : undefined}>
                    <Header totalResults={isFinetuning ? infiniteQueryResult.data?.pages[0]?.totalActivities : totalResults} facetCounts={infiniteQueryResult.data?.pages[0]?.facetCounts} isFetching={infiniteQueryResult.isFetching} showEnginePill={props.showEnginePill} engineName={props.engineName} backgroundColor={background} showDesktopFilterSidebar={showDesktopFilterSidebar} displayParticipants={infiniteQueryResult.data?.pages[0]?.displayParticipants ?? DEFAULT_DISPLAY_PARTICIPANTS} isAi={isAi} />

                    {!showDesktopFilterSidebar && !isAi ? <ActivityFilterBar_SubCategoryRow backgroundType="light" /> : null}

                    {showMobileAiLanding && <ActivityAiMobileLanding />}
                    {/* AI: switching chats refetches the grid via `getByIds` while
                        `keepPreviousData` holds the old cards. Fade them during a full
                        refetch so the swap reads as "loading" instead of a silent, laggy
                        jump. `!isFetchingNextPage` keeps pagination appends from fading the
                        whole grid; skip when the skeleton already covers the load. */}
                    <div ref={stableHeightRef} className={cn('transition-opacity duration-300', showMobileAiLanding && 'hidden lg:block', isAi && infiniteQueryResult.isFetching && !infiniteQueryResult.isFetchingNextPage && !isAiLoading && 'pointer-events-none opacity-50')}>
                        {isAiLoading ? <ActivityAiResultsPlaceholderSkeleton /> : totalResults === 0 ? <ActivityCardGrid.NotFound /> : <ActivityCardGrid.InfiniteScroll adminScores={adminScores} categoryID={props.categoryID} customization={customization} cardVariant={cardVariant} weightSet={weightSet} infiniteQueryResult={infiniteQueryResult} pricedParticipants={infiniteQueryResult.data?.pages[0]?.displayParticipants} allActivities={allActivities}
          // Only when the turn froze an id set — a filters-only
          // turn runs the classic search, which paginates itself.
          onShowMore={isAi && aiActivityIds.length > 0 ? handleShowMore : undefined} minParticipants={minParticipants} isDebugMode={isDebugMode} debugShowSponsoredStyling={customization?.includeAds && (BOOST_AD_QP.debugAdDate !== null || BOOST_AD_QP.debugAdsOnly)} />}
                    </div>
                </ActivityResultsLayout>
                {showMobileAiLanding && <ActivityAiMobileLandingComposer />}
                <ActivityAiMobileCtaBar />
            </motion.div>;
  };
}

/** Top-level re-export of the PLP variant. The `'use client'` boundary in
 *  Next.js Server Components only treats top-level named exports as client
 *  references; consuming `ActivityFilterResult.X` from a server component
 *  yields `undefined`. Server consumers (server.tsx, storyblok) import this. */
export const ActivityFilterResult_InfiniteScroll = ActivityFilterResult.InfiniteScroll;

// Utility function to calculate responsive columns based on container width
/** The reworked card carries more text, so it stops at three columns where the
 * control card goes to four, and it holds one column longer: side by side under
 * ~680px the title, facts, description and price sit on top of each other. */
function getResponsiveLayout(width: number, maxColumns: 3 | 4 = 4): number {
  if (maxColumns === 3) {
    if (width < 680) return 1;
    return width < 900 ? 2 : 3;
  }
  if (width < 500) return 1;
  if (width < 700) return 2;
  if (width < 1100) return 3;
  return 4;
}
const GRID_COLUMN_CLASSES = {
  control: '@min-[500px]:grid-cols-2 @min-[700px]:grid-cols-3 @min-[1100px]:grid-cols-4 grid-cols-1',
  finetuning: '@min-[680px]:grid-cols-2 @min-[900px]:grid-cols-3 grid-cols-1'
} as const;
function getGridGap(width: number): number {
  return width >= 1280 ? 20 : 16;
}

/** Height of the promotion band: one line of everything side by side on a
 * wide grid, stacked on a single-column phone layout. */
const PROMOTION_BAND_HEIGHT = {
  row: 212,
  stacked: 392
} as const;

/** Row of activities a virtual row stands for: rows after the promotion band
 * are shifted by one. */
function activityRowOf(rowIndex: number, promoRowIndex: number | null): number {
  return promoRowIndex !== null && rowIndex > promoRowIndex ? rowIndex - 1 : rowIndex;
}

/** Height of everything under the image. The control card carries a title line
 * and a price; the reworked card adds a two-line description, up to three fact
 * rows, the total line and two buttons. The virtualised grid needs the number
 * up front, so it is fixed per variant rather than measured. */
const CARD_LAYOUT = {
  control: {
    bodyHeight: 75,
    imageRatio: 285 / 365
  },
  finetuning: {
    bodyHeight: 250,
    imageRatio: 250 / 365
  }
} as const;
function calculateCardHeight(gridWidth: number, columns: number, layout: (typeof CARD_LAYOUT)[keyof typeof CARD_LAYOUT]): number {
  const gap = getGridGap(gridWidth);
  const availableWidth = Math.max(gridWidth, 0);
  const cardWidth = (availableWidth - (columns - 1) * gap) / columns;
  const imageWidth = cardWidth - 24;
  return 24 + imageWidth * layout.imageRatio + layout.bodyHeight + gap;
}

// Hook to track window width using useLayoutEffect
function useWindowWidth(t0) {
  const $ = _c(2);
  const fallbackWidth = t0 === undefined ? 375 : t0;
  const [windowWidth, setWindowWidth] = useState(fallbackWidth);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const handleResize = function handleResize() {
        setWindowWidth(window.innerWidth);
      };
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useLayoutEffect(t1, t2);
  return windowWidth;
}
function useMeasuredWidth(fallbackWidth) {
  const $ = _c(4);
  const ref = useRef(null);
  const [width, setWidth] = useState(fallbackWidth);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const element = ref.current;
      if (!element) {
        return;
      }
      const updateWidth = () => {
        setWidth(element.getBoundingClientRect().width);
      };
      updateWidth();
      if (typeof ResizeObserver === "undefined") {
        return;
      }
      const observer = new ResizeObserver(entries => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        setWidth(entry.contentRect.width);
      });
      observer.observe(element);
      return () => observer.disconnect();
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useLayoutEffect(t0, t1);
  let t2;
  if ($[2] !== width) {
    t2 = [ref, width];
    $[2] = width;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2 as const;
}

/**
 * Preserves the container height during data transitions to prevent scroll jumps.
 * When `isFetching` becomes true, the current height is locked as a min-height.
 * Once data settles, the min-height is released.
 */
function useStableHeight(isFetching) {
  const $ = _c(3);
  const containerRef = useRef(null);
  const lockedHeightRef = useRef(null);
  let t0;
  let t1;
  if ($[0] !== isFetching) {
    t0 = () => {
      const el = containerRef.current;
      if (!el) {
        return;
      }
      if (isFetching) {
        lockedHeightRef.current = el.getBoundingClientRect().height;
        el.style.minHeight = `${lockedHeightRef.current}px`;
      } else {
        lockedHeightRef.current = null;
        el.style.minHeight = "";
      }
    };
    t1 = [isFetching];
    $[0] = isFetching;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useLayoutEffect(t0, t1);
  return containerRef;
}
const ACTIVITIES_PER_PAGE = 24;
namespace ActivityCardGrid {
  export const InfiniteScroll: React.FC<{
    categoryID?: number;
    customization?: ActivitySearchPage.SearchCustomizationProps;
    cardVariant: PostHog_FeaturesSDK.Variant<'plp-cards'>;
    weightSet: PostHog_FeaturesSDK.Variant<'alternate-elastic-weights'>;
    infiniteQueryResult: UseInfiniteQueryResult<InfiniteData<TRPC_ROUTER.output<typeof TRPC_ROUTER.activities.search>>, Error>;
    allActivities: TRPC_ROUTER.output<typeof TRPC_ROUTER.activities.search>['activities'];
    minParticipants: number | null;
    /** The group size the listed prices were calculated at, from the payload. */
    pricedParticipants: number | undefined;
    /** Skip tracking clicks when in debug mode */
    isDebugMode?: boolean;
    debugShowSponsoredStyling?: boolean | null | undefined;
    /** Admin-only AI relevancy/popularity scores, keyed by activity id. */
    adminScores?: Record<number, AiActivityScore>;
    /**
     * AI mode only: when set, the footer shows a "show more activities"
     * action that asks the chat to widen the current result set. Omitted in
     * filter mode (infinite scroll already loads everything there).
     */
    onShowMore?: () => void;
    /** Replaces the default `<ActivityCard>` in every grid cell. The BIZZ
     *  booking catalog passes its edit-mode card; the visitor-facing extras
     *  (boosted ads, click tracking, distance labels) are skipped with it. */
    renderCard?: (activity: ActivityItem) => React.ReactNode;
    /** Measure each row instead of sizing it from `calculateCardHeight`.
     *  Required whenever `renderCard` returns a card taller than the PLP's:
     *  the card is `h-full overflow-hidden`, so a row sized by the formula
     *  silently clips whatever the formula did not budget for — the BIZZ
     *  edit card's "Add to catalog" footer, for one. */
    measureRows?: boolean;
  }> = props => {
    const i18n = useI18n();
    const portal = usePortalCtx();
    const posthog = usePostHog();
    const {
      isAi
    } = useActivitySearchMode();
    const filterValues = useActivitySearchCtx().useFilters().value;
    const routeFilter = filterValues.location;
    const searchRadiusKm = filterValues.radius;
    const isFinetuning = usePlpExperiment();
    const participants = filterValues.participants ?? DEFAULT_DISPLAY_PARTICIPANTS;
    const injectedCard = resolveInjectedCard({
      portal: portal.portal,
      isFinetuning,
      participants
    });
    const windowWidth = useWindowWidth();
    const [gridRef, gridWidth] = useMeasuredWidth<HTMLDivElement>(windowWidth);
    const ROW_GAP_PX = 16;
    // The reworked PLP card is measured because its body grows with the
    // text; a caller-supplied card is measured because we cannot know how
    // tall it is. Column count stays tied to the experiment, not to this.
    const measureRows = isFinetuning || (props.measureRows ?? false);

    // Calculate all responsive values together to avoid stale state
    const layoutValues = useMemo(() => {
      const effectiveWidth = gridWidth || windowWidth;
      const cols = getResponsiveLayout(effectiveWidth, isFinetuning ? 3 : 4);
      const desiredInjectedPos = cols === 1 ? 2 : cols - 1;
      const safeInjectedPos = Math.min(desiredInjectedPos, props.allActivities.length);
      // The USP card takes one slot in a row; the promotion band is a row
      // of its own after the first row of results.
      const showInjected = injectedCard === 'usp' && (cols === 1 || props.allActivities.length >= cols);
      const promoRow = injectedCard === 'promotion' && props.allActivities.length >= cols ? 1 : null;
      const items = props.allActivities.length + (showInjected ? 1 : 0);
      const rows = Math.ceil(items / cols) + (promoRow === null ? 0 : 1);
      const cardHeight = Math.ceil(calculateCardHeight(effectiveWidth, cols, CARD_LAYOUT[isFinetuning ? 'finetuning' : 'control']));
      return {
        columns: cols,
        shouldShowInjectedCard: showInjected,
        rowCount: rows,
        CARD_HEIGHT: cardHeight,
        ROW_HEIGHT: cardHeight + ROW_GAP_PX,
        PROMO_ROW_HEIGHT: PROMOTION_BAND_HEIGHT[cols === 1 ? 'stacked' : 'row'] + ROW_GAP_PX,
        promoRowIndex: promoRow,
        injectedCardPosition: safeInjectedPos
      };
    }, [gridWidth, windowWidth, injectedCard, isFinetuning, props.allActivities.length, ROW_GAP_PX]);
    const {
      columns
    } = layoutValues;
    const {
      shouldShowInjectedCard
    } = layoutValues;
    const {
      rowCount
    } = layoutValues;
    const {
      CARD_HEIGHT
    } = layoutValues;
    const {
      ROW_HEIGHT
    } = layoutValues;
    const {
      PROMO_ROW_HEIGHT
    } = layoutValues;
    const {
      promoRowIndex
    } = layoutValues;
    const {
      injectedCardPosition
    } = layoutValues;
    const rowVirtualizer = useWindowVirtualizer({
      count: rowCount,
      estimateSize: index => index === promoRowIndex ? PROMO_ROW_HEIGHT : ROW_HEIGHT,
      overscan: 3,
      scrollMargin: 0,
      paddingEnd: 16,
      getScrollElement: () => window
    });

    // Force remeasure on dimension changes (when card height changes due to resize).
    // Measured rows re-observe themselves, and this call would replace their
    // measurements with the estimate until the next scroll.
    useLayoutEffect(() => {
      if (measureRows) return;
      requestAnimationFrame(() => {
        rowVirtualizer.measure();
      });
    }, [rowVirtualizer, measureRows, CARD_HEIGHT, PROMO_ROW_HEIGHT, promoRowIndex, windowWidth, columns]);

    // Track refs for scroll position checking
    const columnsRef = useRef(columns);
    const shouldShowInjectedCardRef = useRef(shouldShowInjectedCard);
    const injectedCardPositionRef = useRef(injectedCardPosition);
    const promoRowIndexRef = useRef(promoRowIndex);
    // `scroll: false` is critical: this param is written FROM the scroll
    // position as the user scrolls. Without it, nuqs/Next runs default scroll
    // restoration on every update and yanks the page to a random offset.
    // `shallow` + `history: 'replace'` keep it client-only and history-clean.
    const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(0).withOptions({
      shallow: true,
      history: 'replace',
      scroll: false
    }));
    const pageRef = useRef(page);
    const firstLoadedPageNum = props.infiniteQueryResult.data?.pageParams[0] as number | undefined ?? 0;
    const firstLoadedPageNumRef = useRef(firstLoadedPageNum);
    useLayoutEffect(() => {
      columnsRef.current = columns;
      shouldShowInjectedCardRef.current = shouldShowInjectedCard;
      injectedCardPositionRef.current = injectedCardPosition;
      promoRowIndexRef.current = promoRowIndex;
      pageRef.current = page;
      firstLoadedPageNumRef.current = firstLoadedPageNum;
    }, [columns, shouldShowInjectedCard, injectedCardPosition, promoRowIndex, page, firstLoadedPageNum]);
    const debouncedSetPage = useDebouncedCallback((newPage: number) => {
      setPage(newPage);
    }, 300, [setPage]);

    // Declarative fetch-next-page trigger. Re-evaluates on every render, so
    // it stays correct across scroll, resize/remeasure, and query state
    // transitions (hasNextPage undefined -> true, isFetchingNextPage
    // true -> false). This replaces a scroll-event + ref-gate approach that
    // could get permanently stuck at the bottom if the first debounced
    // fetch was skipped because hasNextPage/isFetchingNextPage hadn't
    // settled yet. Decision logic lives in `shouldFetchNextPage` so it can
    // be unit-tested without a DOM.
    // https://tanstack.com/virtual/latest/docs/framework/react/examples/infinite-scroll
    const MAX_AUTO_RETRIES = 3;
    const retryCountRef = useRef(0);
    const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Tracks whether a scheduled retry is pending to prevent stacking timers.
    const retryScheduledRef = useRef(false);
    const [retriesExhausted, setRetriesExhausted] = useState(false);
    const virtualItems = rowVirtualizer.getVirtualItems();
    useEffect(() => {
      const {
        isFetchNextPageError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
      } = props.infiniteQueryResult;

      // Happy path: no error — reset retry state and fetch normally.
      if (!isFetchNextPageError) {
        if (retryCountRef.current > 0) {
          retryCountRef.current = 0;
          retryScheduledRef.current = false;
          setRetriesExhausted(false);
        }
        if (shouldFetchNextPage({
          lastItemIndex: virtualItems[virtualItems.length - 1]?.index,
          rowCount,
          hasNextPage,
          isFetchingNextPage
        })) {
          fetchNextPage();
        }
        return;
      }

      // Error path: schedule an automatic retry with exponential backoff,
      // up to MAX_AUTO_RETRIES. After that, surface the manual retry button.
      if (retryCountRef.current >= MAX_AUTO_RETRIES) {
        setRetriesExhausted(true);
        return;
      }
      if (retryScheduledRef.current) return;
      retryScheduledRef.current = true;
      const delay = 1000 * 2 ** retryCountRef.current; // 1 s, 2 s, 4 s
      retryTimeoutRef.current = setTimeout(() => {
        retryCountRef.current += 1;
        retryScheduledRef.current = false;
        fetchNextPage();
      }, delay);
    }, [virtualItems, rowCount, props.infiniteQueryResult.hasNextPage, props.infiniteQueryResult.isFetchingNextPage, props.infiniteQueryResult.fetchNextPage, props.infiniteQueryResult.isFetchNextPageError]);

    // Clear any pending retry timer on unmount.
    useEffect(() => () => {
      if (retryTimeoutRef.current !== null) {
        clearTimeout(retryTimeoutRef.current);
      }
    }, []);

    // Scroll position checking interval for syncing the `page` URL param
    // with the currently-visible row.
    //
    // Uses the row whose top is at-or-above the viewport top (the topmost
    // row that's actually been scrolled into view), instead of the first
    // virtualized item — which can be an overscan row above the viewport
    // and would flip the page param prematurely. A small hysteresis
    // threshold keeps the URL stable when the user wiggles right at a
    // page boundary.
    useLayoutEffect(() => {
      const PAGE_FLIP_HYSTERESIS_PX = 80;
      const checkScrollPosition = throttle(() => {
        const items_0 = rowVirtualizer.getVirtualItems();
        if (items_0.length === 0) return;
        const {
          scrollY
        } = window;
        // Row index to attribute to "page in view": last row whose
        // top has scrolled past the viewport top, falling back to
        // the first virtualized row when nothing has scrolled past
        // yet (top of the page).
        let topRowIndex = items_0[0]!.index;
        for (const item of items_0) {
          if (item.start <= scrollY) topRowIndex = item.index;else break;
        }
        const firstActivityIndex = activityRowOf(topRowIndex, promoRowIndexRef.current) * columnsRef.current;
        const adjustedIndex = shouldShowInjectedCardRef.current && firstActivityIndex > injectedCardPositionRef.current ? firstActivityIndex - 1 : firstActivityIndex;
        const pageOffsetFromFirst = Math.floor(adjustedIndex / ACTIVITIES_PER_PAGE);
        const currentPageInView = firstLoadedPageNumRef.current + pageOffsetFromFirst;
        if (currentPageInView === pageRef.current) return;

        // Hysteresis: only commit a page change once we've moved at
        // least N px past the boundary row's top. Prevents the URL
        // from ping-ponging when the user lingers right at the edge.
        const boundaryRow = items_0.find(item_0 => {
          const idx = activityRowOf(item_0.index, promoRowIndexRef.current) * columnsRef.current;
          const adj = shouldShowInjectedCardRef.current && idx > injectedCardPositionRef.current ? idx - 1 : idx;
          const itemPage = firstLoadedPageNumRef.current + Math.floor(adj / ACTIVITIES_PER_PAGE);
          return itemPage === currentPageInView;
        });
        if (boundaryRow) {
          const distance = currentPageInView > pageRef.current ? scrollY - boundaryRow.start : boundaryRow.start + boundaryRow.size - scrollY;
          if (distance < PAGE_FLIP_HYSTERESIS_PX) return;
        }
        pageRef.current = currentPageInView;
        debouncedSetPage(currentPageInView);
      }, 200);
      window.addEventListener('scroll', checkScrollPosition);
      checkScrollPosition();
      return () => window.removeEventListener('scroll', checkScrollPosition);
    }, [debouncedSetPage, rowVirtualizer]);

    // Loading state with responsive skeleton count
    if (props.infiniteQueryResult.status === 'pending') {
      const effectiveWidth_0 = gridWidth || windowWidth;
      const skeletonColumns = getResponsiveLayout(effectiveWidth_0, isFinetuning ? 3 : 4);
      const skeletonRows = 6;
      const skeletonCount = skeletonColumns * skeletonRows;
      const skeletonCardHeight = Math.ceil(calculateCardHeight(effectiveWidth_0, skeletonColumns, CARD_LAYOUT[isFinetuning ? 'finetuning' : 'control']));
      return (
        // `@container` on the ref'd wrapper; the `@min-[Npx]:` column variants
        // resolve against an ancestor container, so they must live on a CHILD
        // (not the same element) or the grid collapses to one column.
        <div ref={gridRef} className="@container">
                    <div className={cn('grid gap-4', GRID_COLUMN_CLASSES[isFinetuning ? 'finetuning' : 'control'])}>
                        {Array.from({
              length: skeletonCount
            }).map((_, i) => <ActivityCard_Skeleton
            // eslint-disable-next-line react/no-array-index-key -- static placeholder list, never reordered
            key={i} style={{
              height: `${skeletonCardHeight}px`
            }} />)}
                    </div>
                </div>
      );
    }

    // Error state
    if (props.infiniteQueryResult.status === 'error') {
      return <div className="rounded-3xl bg-white p-6 text-center">
                    <p className="text-destructive">
                        {i18n._('search.error.loadingactivities', undefined, {
            message: 'Error loading activities..'
          })}
                    </p>
                </div>;
    }
    return <div ref={gridRef} className="@container relative mb-6">
                {/* Loading indicator for previous pages */}
                {props.infiniteQueryResult.isFetchingPreviousPage && <div className="flex flex-col items-center justify-center gap-2 py-2">
                        <Icon name="spinner" className="h-8 w-8 animate-spin rounded-full" />
                        <p className="text-sm text-gray-500">
                            {i18n._('search.loading.previousactivities', undefined, {
            message: 'Loading previous activities..'
          })}
                        </p>
                    </div>}

                {/* Virtualized content */}
                <div style={{
        height: `${rowVirtualizer.getTotalSize()}px`,
        width: '100%',
        position: 'relative'
      }}>
                    {rowVirtualizer.getVirtualItems().map(virtualRow => {
          if (virtualRow.index === promoRowIndex) {
            return <div key={virtualRow.key} data-index={virtualRow.index}
            // Measured like the card rows: the band stacks below
            // the md breakpoint while the grid may still show two
            // columns, so the estimate only seeds the height.
            ref={rowVirtualizer.measureElement} style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              paddingTop: ROW_GAP_PX,
              paddingBottom: ROW_GAP_PX * 2,
              transform: `translateY(${virtualRow.start}px)`
            }}>
                                    <PlpPromotionCard participants={participants} activityCount={props.infiniteQueryResult.data?.pages[0]?.totalActivities ?? props.allActivities.length} />
                                </div>;
          }
          const startIndex = activityRowOf(virtualRow.index, promoRowIndex) * columns;

          // Build row activities, inserting USP card at configured global index
          const rowActivities: Array<TRPC_ROUTER.output<typeof TRPC_ROUTER.activities.search>['activities'][number] | 'INJECTED'> = [];
          for (let i_0 = 0; i_0 < columns; i_0++) {
            const globalIndex = startIndex + i_0;
            if (shouldShowInjectedCard && globalIndex === injectedCardPosition) {
              rowActivities.push('INJECTED');
              continue;
            }
            const activityIndex = shouldShowInjectedCard && globalIndex > injectedCardPosition ? globalIndex - 1 : globalIndex;
            const activity = props.allActivities[activityIndex];
            if (activity) {
              rowActivities.push(activity);
            }
          }
          return <div key={virtualRow.key} data-index={virtualRow.index}
          // The variant's cards keep their own height, so the row is
          // measured instead of fixed; the estimate only seeds it.
          ref={measureRows ? rowVirtualizer.measureElement : undefined} style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: measureRows ? undefined : `${CARD_HEIGHT}px`,
            paddingBottom: measureRows ? ROW_GAP_PX : undefined,
            transform: `translateY(${virtualRow.start}px)`
          }}>
                                <div className={cn('grid items-stretch gap-4 xl:gap-5', !measureRows && 'h-full xl:pb-5', GRID_COLUMN_CLASSES[isFinetuning ? 'finetuning' : 'control'])}>
                                    {rowActivities.map((item_1, rowIdx) => {
                if (item_1 === 'INJECTED') {
                  return <USPCard key="injected-card" className="h-full" gridColumns={columns} />;
                }
                if (props.renderCard) {
                  return <React.Fragment key={item_1.id}>
                                                    {props.renderCard(item_1)}
                                                </React.Fragment>;
                }
                const adId = item_1.boostedAdID;
                const isBoostAd = typeof adId === 'number';
                // Compute rendered position: row start + position in row (adjusted for USP)
                const activityIndex_0 = shouldShowInjectedCard && startIndex + rowIdx > injectedCardPosition ? startIndex + rowIdx - 1 : startIndex + rowIdx;
                return <ActivityCard {...item_1} key={isBoostAd && adId ? `ad-${adId}` : item_1.id} boostedAdID={adId} adminScore={props.adminScores?.[Number(item_1.id)]} routeFilter={routeFilter} searchRadiusKm={searchRadiusKm} openIn={columns <= 1 ? 'modal' : 'new-tab'} debugShowSponsoredStyling={props.debugShowSponsoredStyling ?? undefined} selectedParticipants={props.minParticipants} pricedParticipants={props.pricedParticipants} onClick={() => {
                  // Skip tracking when in debug mode to avoid fake traffic
                  if (props.isDebugMode) return;
                  posthog.capture('activity.click', {
                    scope: mapUnion(portal.portal, {
                      BIZZ: 'BIZZ',
                      ADMIN: 'ADMIN',
                      PARTNER: 'PARTNER',
                      WEB: 'WEB'
                    }),
                    activityId: item_1.id,
                    categoryId: props?.categoryID ?? null,
                    boostAdID: isBoostAd ? adId : null,
                    renderedPosition: activityIndex_0 + 1,
                    // 1-based
                    search_mode: isAi ? 'ai' : 'filters'
                  });
                }} />;
              })}
                                </div>
                            </div>;
        })}
                </div>

                {/* Reserved-height footer region (FUN-4558): the spinner / retry /
                    "all loaded" blocks are mutually exclusive but have different
                    natural heights. Swapping them as a fetch starts/finishes used to
                    grow/shrink this region, nudging the scroll anchor and re-triggering
                    the auto-fetch (ping-pong). A single fixed min-height container with
                    centered content keeps the layout height constant across all three
                    states, so a single fetch loads one page and settles. */}
                {(props.infiniteQueryResult.isFetchingNextPage || retriesExhausted && props.infiniteQueryResult.isFetchNextPageError || !props.infiniteQueryResult.hasNextPage && props.allActivities.length > 0) && <div className="flex min-h-[88px] flex-col items-center justify-center gap-2 text-center">
                        {props.infiniteQueryResult.isFetchingNextPage ? <>
                                <Icon name="spinner" className="h-8 w-8 animate-spin rounded-full" />
                                <p className="text-sm text-gray-500">
                                    {i18n._('search.loading.moreactivities', undefined, {
              message: 'Loading more activities..'
            })}
                                </p>
                            </> : retriesExhausted && props.infiniteQueryResult.isFetchNextPageError ? <>
                                <p className="text-sm text-destructive">
                                    {i18n._('search.error.loadingactivities', undefined, {
              message: 'Error loading activities..'
            })}
                                </p>
                                <Button variant="outline" size="sm" onClick={() => {
            retryCountRef.current = 0;
            retryScheduledRef.current = false;
            setRetriesExhausted(false);
            props.infiniteQueryResult.fetchNextPage();
          }}>
                                    {i18n._('search.retry', undefined, {
              message: 'Try again'
            })}
                                </Button>
                            </> : <>
                                <span className="text-sm text-gray-500">
                                    {i18n._('search.loading.allactivitiesloaded', {
              count: props.allActivities.length
            })}
                                </span>
                                {props.onShowMore && <button type="button" onClick={props.onShowMore} className="font-medium text-primary underline underline-offset-2 hover:no-underline">
                                        {i18n._('activity.search.ai.results.showMore', undefined, {
              message: 'Show more activities'
            })}
                                    </button>}
                            </>}
                    </div>}
            </div>;
  };

  /** Activity item that may be a boosted ad */
  export type ActivityItem = TRPC_ROUTER.Outputs['activities']['search']['activities'][number];
  export const Component: React.FC<Pick<React.ComponentProps<typeof ActivityCard>, 'debugShowSponsoredStyling'> & {
    categoryID?: number;
    backgroundColor?: VariantProps<typeof backgroundColorVariant>['backgroundColor'];
    minParticipants: number | null;
    /** The group size the listed prices were calculated at, from the payload. */
    pricedParticipants: number | undefined;
    /** Distinct teambuildings across all pages, for the promotion band.
     * Not `pageInfo.totalResults`: that counts a sponsored card twice,
     * so the band would advertise a different number than the header. */
    promotionActivityCount?: number;

    /** The index of the page. */
    pageIdx: number;

    /** Category IDs from the search filters */
    categoryFilterIds?: number[];

    /** Skip tracking clicks when in debug mode */
    isDebugMode?: boolean;

    /** Admin-only AI relevancy/popularity scores, keyed by activity id. */
    adminScores?: Record<number, AiActivityScore>;
    activities: {
      /** Is fetching new data.
       * Is `false` when navigating via filters or pagination */
      isFetched: boolean;
      data?: TRPC_ROUTER.output<typeof TRPC_ROUTER.activities.search>['activities'];
    };
  }> = props => {
    const portal = usePortalCtx();
    const posthog = usePostHog();
    const {
      isAi
    } = useActivitySearchMode();
    const filterValues = useActivitySearchCtx().useFilters().value;
    const routeFilter = filterValues.location;
    const searchRadiusKm = filterValues.radius;
    const isFinetuning = usePlpExperiment();
    const participants = filterValues.participants ?? DEFAULT_DISPLAY_PARTICIPANTS;
    const injectedCard = resolveInjectedCard({
      portal: portal.portal,
      isFinetuning,
      participants
    });

    // Calculate responsive layout for the injected card's position
    const windowWidth = useWindowWidth();
    const [gridRef, gridWidth] = useMeasuredWidth<HTMLDivElement>(windowWidth);
    const layoutValues = useMemo(() => {
      const effectiveWidth = gridWidth || windowWidth;
      const columns = getResponsiveLayout(effectiveWidth, isFinetuning ? 3 : 4);
      const cardHeight = Math.ceil(calculateCardHeight(effectiveWidth, columns, CARD_LAYOUT[isFinetuning ? 'finetuning' : 'control']));
      const shouldShowInjectedCard = injectedCard !== null && props.activities.data && (columns === 1 || props.activities.data.length >= columns);
      return {
        columns,
        shouldShowInjectedCard,
        // The USP card takes a slot inside the first row; the promotion
        // band gets a row of its own right after it.
        injectedCardPosition: injectedCard === 'promotion' ? columns : columns === 1 ? 2 : columns - 1,
        cardHeight
      };
    }, [gridWidth, windowWidth, injectedCard, isFinetuning, props.activities.data?.length]);
    const skeletonColumns = layoutValues.columns;
    const skeletonCount = skeletonColumns * 6;

    // Prepare activities with USP card positioned correctly
    const activitiesWithInjectedCard = useMemo(() => {
      if (!props.activities.data || !layoutValues.shouldShowInjectedCard || props.pageIdx !== 0) {
        return props.activities.data || [];
      }
      const activities: Array<TRPC_ROUTER.output<typeof TRPC_ROUTER.activities.search>['activities'][number] | 'INJECTED'> = [...props.activities.data];
      activities.splice(layoutValues.injectedCardPosition, 0, 'INJECTED');
      return activities;
    }, [props.activities.data, layoutValues.shouldShowInjectedCard, layoutValues.injectedCardPosition, props.pageIdx]);

    // Disable fade-in on initial render if has SSR data
    const shouldFadeInRef = useRef(false);
    useEffect(() => {
      shouldFadeInRef.current = true;
    }, []);
    const CARD_ANIMATION_VARIANTS = {
      hidden: {
        opacity: 0,
        y: 20
      },
      visible: {
        opacity: 1,
        y: 0
      }
    };
    return <div ref={gridRef} className="@container min-w-0">
                <motion.main initial={shouldFadeInRef.current ? 'hidden' : 'visible'} animate="visible" variants={{
        hidden: {
          opacity: 0
        },
        visible: {
          opacity: 1,
          transition: {
            delayChildren: stagger(0.015),
            ease: 'easeOut'
          }
        }
      }} className={cn('relative mb-10 grid items-stretch gap-4 xl:gap-5', !isFinetuning && '@min-[500px]:auto-rows-fr', GRID_COLUMN_CLASSES[isFinetuning ? 'finetuning' : 'control'])}>
                    {!props.activities.isFetched ? Array.from({
          length: skeletonCount
        }, (_, i) => <motion.div key={i} style={{
          height: 334
        }} variants={CARD_ANIMATION_VARIANTS}>
                                  <ActivityCard_Skeleton className="h-full" />
                              </motion.div>) : activitiesWithInjectedCard.map((item, index) => {
          if (item === 'INJECTED') {
            return <motion.div key="injected-card" className={cn('h-full overflow-hidden', injectedCard === 'promotion' && 'col-span-full py-4')} variants={CARD_ANIMATION_VARIANTS} transition={{
              delay: 0.05
            }}>
                                          {injectedCard === 'promotion' ? <PlpPromotionCard className="h-full" participants={participants} activityCount={props.promotionActivityCount ?? props.activities.data?.length ?? 0} /> : <USPCard className="h-full" gridColumns={layoutValues.columns} />}
                                      </motion.div>;
          }
          const adId = item.boostedAdID;
          const isBoostAd = typeof adId === 'number';
          return <motion.div key={isBoostAd && adId ? `ad-${adId}` : item.id} className="h-full" variants={{
            hidden: {
              opacity: 0,
              y: 20
            },
            visible: {
              opacity: 1,
              y: 0
            }
          }}>
                                      <ActivityCard {...item} boostedAdID={adId} adminScore={props.adminScores?.[Number(item.id)]} routeFilter={routeFilter} searchRadiusKm={searchRadiusKm} debugShowSponsoredStyling={props.debugShowSponsoredStyling} selectedParticipants={props.minParticipants} pricedParticipants={props.pricedParticipants} onClick={() => {
              // Skip tracking when in debug mode to avoid fake traffic
              if (props.isDebugMode) return;
              const scope = mapUnion(portal.portal, {
                BIZZ: 'BIZZ',
                ADMIN: 'ADMIN',
                PARTNER: 'PARTNER',
                WEB: 'WEB'
              } as const);
              posthog.capture('activity.click', {
                scope,
                activityId: item.id,
                categoryId: props?.categoryFilterIds?.[0] ?? null,
                boostAdID: isBoostAd ? adId : null,
                renderedPosition: index + 1,
                // 1-based
                search_mode: isAi ? 'ai' : 'filters'
              });
            }} />
                                  </motion.div>;
        })}
                </motion.main>
            </div>;
  };
  const USPCard: React.FC<{
    className?: ClassValue;
    /** Number of grid columns from the parent layout — used to derive card size
     *  without an internal ResizeObserver (avoids flicker at breakpoint boundaries). */
    gridColumns?: number;
  }> = props => {
    const $ = _c(20);
    const allItems = usePlpUsps();
    const size = props.gridColumns != null ? props.gridColumns >= 4 ? "lg" : props.gridColumns >= 3 ? "md" : "sm" : "sm";
    const maxVisible = size === "lg" ? 3 : size === "md" ? 3 : 4;
    let t0;
    let t1;
    let t2;
    if ($[0] !== allItems || $[1] !== maxVisible || $[2] !== props.className || $[3] !== size) {
      const visibleItems = allItems.slice(0, maxVisible);
      const t3 = size === "lg" ? "p-6" : size === "md" ? "p-5" : "p-4";
      if ($[7] !== props.className || $[8] !== t3) {
        t2 = cn("shadow-xs flex flex-col justify-center overflow-hidden rounded-3xl bg-white", t3, props.className);
        $[7] = props.className;
        $[8] = t3;
        $[9] = t2;
      } else {
        t2 = $[9];
      }
      const t4 = size === "lg" ? "gap-3" : size === "md" ? "gap-3" : "gap-2";
      if ($[10] !== t4) {
        t0 = cn("grid", t4);
        $[10] = t4;
        $[11] = t0;
      } else {
        t0 = $[11];
      }
      let t5;
      if ($[12] !== size) {
        t5 = item => <USPItem key={item.icon} title={item.title} description={item.description} icon={item.icon} size={size} />;
        $[12] = size;
        $[13] = t5;
      } else {
        t5 = $[13];
      }
      t1 = visibleItems.map(t5);
      $[0] = allItems;
      $[1] = maxVisible;
      $[2] = props.className;
      $[3] = size;
      $[4] = t0;
      $[5] = t1;
      $[6] = t2;
    } else {
      t0 = $[4];
      t1 = $[5];
      t2 = $[6];
    }
    let t3;
    if ($[14] !== t0 || $[15] !== t1) {
      t3 = <ul className={t0}>{t1}</ul>;
      $[14] = t0;
      $[15] = t1;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    let t4;
    if ($[17] !== t2 || $[18] !== t3) {
      t4 = <div className={t2}>{t3}</div>;
      $[17] = t2;
      $[18] = t3;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    return t4;
  };
  const USPItem: React.FC<{
    title: React.ReactNode;
    icon: Icon.Name;
    description: React.ReactNode;
    size: 'sm' | 'md' | 'lg';
  }> = props => {
    const $ = _c(18);
    const t0 = props.size === "sm" ? "gap-2" : "gap-3";
    let t1;
    if ($[0] !== t0) {
      t1 = cn("flex items-start", t0);
      $[0] = t0;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const t2 = props.icon;
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = cn("h-auto text-tertiary-500", "size-5!");
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] !== props.icon) {
      t4 = <figure className="shrink-0"><Icon strokeWidth={3} name={t2} viewBox="0 0 48 48" size="lg" className={t3} /></figure>;
      $[3] = props.icon;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = cn("leading-snug text-blue", "text-sm");
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] !== props.title) {
      t6 = <strong className={t5}>{props.title}</strong>;
      $[6] = props.title;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = cn("leading-relaxed text-blue-500", "text-xs");
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    let t8;
    if ($[9] !== props.description) {
      t8 = <p className={t7}>{props.description}</p>;
      $[9] = props.description;
      $[10] = t8;
    } else {
      t8 = $[10];
    }
    let t9;
    if ($[11] !== t6 || $[12] !== t8) {
      t9 = <div className="flex min-w-0 flex-col">{t6}{t8}</div>;
      $[11] = t6;
      $[12] = t8;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    let t10;
    if ($[14] !== t1 || $[15] !== t4 || $[16] !== t9) {
      t10 = <li className={t1}>{t4}{t9}</li>;
      $[14] = t1;
      $[15] = t4;
      $[16] = t9;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    return t10;
  };
  export const NotFound: React.FC = () => {
    const ctx = useActivitySearchCtx();
    const filters = ctx.useFilters();
    const i18n = useI18n();
    const {
      isAi
    } = useActivitySearchMode();
    const aiEnabled = useAiSearchEnabled();

    // Portals without AI Search (BIZZ) get the plain shared empty state — no AI
    // pitch copy and no AI handoff composer.
    if (!aiEnabled) return <ActivityCardGrid_NotFound />;
    return <div className="animate-fade-in my-4 overflow-hidden rounded-3xl bg-white duration-200">
                {/* soft gradient halo above the composer to draw the eye */}
                <div className="relative px-6 pb-8 pt-10 text-center sm:px-12 sm:pb-12 sm:pt-16">
                    <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 -z-0 h-72 bg-[radial-gradient(60%_60%_at_50%_0%,var(--color-tertiary-50)_0%,transparent_70%)]" />
                    <div className="relative z-10 flex flex-col items-center">
                        <span className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-stone-200/90 bg-stone-50 px-3 py-1 text-[11px] font-medium text-stone-600">
                            <Icon name="search" className="size-3.5 text-stone-400" strokeWidth={2.5} />
                            {i18n._('web.activities.not_found.ai.badge', undefined, {
              message: 'No matches for these filters'
            })}
                        </span>
                        <Title size="2xl" className="max-w-xl">
                            {i18n._('web.activities.not_found.ai.title', undefined, {
              message: 'Tell us what you really want.'
            })}
                        </Title>
                        <p className="mt-3 max-w-md text-sm text-blue-500/80 sm:text-base">
                            {i18n._('web.activities.not_found.ai.description', undefined, {
              message: 'Describe your ideal team event — our AI will surface activities that fit, even when filters fall short.'
            })}
                        </p>

                        {isAi ? null : <ActivityAiSearchFilterPanel surface="light" className="mt-8 w-full max-w-2xl" />}

                        <button type="button" onClick={filters.clear} className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-stone-500 underline-offset-4 transition-colors hover:text-stone-800 hover:underline">
                            <X className="size-3.5" strokeWidth={2.5} aria-hidden />
                            {i18n._('web.activities.not_found.ai.clear', undefined, {
              message: 'Or clear all filters'
            })}
                        </button>
                    </div>
                </div>
            </div>;
  };
}

/** The virtualized infinite-scroll grid on its own, without the PLP's header,
 *  sidebar and AI panel. The BIZZ booking catalog drives it with its own query
 *  and its own card. Same `'use client'` boundary reason as the re-export
 *  above, and it has to sit BELOW the namespace: a namespace object only
 *  exists once its block has run, so reading it earlier yields `undefined`
 *  and every module importing this one throws on import. */
export const ActivityCardGrid_InfiniteScroll = ActivityCardGrid.InfiniteScroll;
const ActiveFilterPills: React.FC<{
  backgroundType: 'light' | 'dark';
  hideOnDesktop?: boolean;
  /** Left-aligned under the results count, ending in "Clear all". Under the
   * filter bar the pills stay centred and clearing lives elsewhere. */
  placement?: 'under-filter-bar' | 'results-header';
}> = props => {
  const i18n = useI18n();
  const posthog = usePostHog();
  const ctx = useActivitySearchCtx();
  const allFilters = ctx.useFilters();
  const categoriesData = ACTIVITY_FILTER_BAR_PRESET.FILTERS[3].useDataLoader(allFilters.value as Record<string, unknown>);
  const regionsData = useRegionsQuery();
  const languagesData = useLanguagesQuery();
  const showExtraFilters = PostHog_ClientFeaturesSDK.getFlag(posthog, 'plp-extra-filters', 'control') === 'enabled';
  const lookupData = useMemo(() => ({
    categories: categoriesData,
    regions: regionsData,
    languages: languagesData
  }), [categoriesData, regionsData, languagesData]);
  const isFinetuning = usePlpExperiment();
  const hasPrefilledNearPoint = useHasPrefilledNearPoint();
  const activeFilterPills = useMemo(() => buildActiveFilterPills({
    values: allFilters.value,
    data: lookupData,
    i18n,
    showExtraFilters,
    hasSubCategoryRow: !isFinetuning
  }), [allFilters.value, lookupData, i18n, showExtraFilters, isFinetuning]);
  const inResultsHeader = props.placement === 'results-header';
  const pillButtons = <ActiveFilterPillButtons pills={activeFilterPills} inResultsHeader={inResultsHeader} backgroundType={props.backgroundType} onRemove={pill => allFilters.update(pill.keys.includes('nearLabel') ?
  // A city page starts with a place already filled in, so nulling
  // the fields is not enough to clear it: it needs the flag that
  // says the visitor took it off. Anything merged into the same
  // pill — a region page's own region, under the same name —
  // rides along, since one pill takes off one place.
  NEAR_POINT_CLEARED({
    hasPrefilledNearPoint,
    extra: std.Object.omit(pill.remove, ['nearLabel', 'nearLat', 'nearLng', 'radius'])
  }) : pill.remove)} onClear={allFilters.clear} />;
  if (!inResultsHeader && activeFilterPills.length === 0) return null;
  if (inResultsHeader) {
    // Height animates so the cards slide rather than jump when a pill
    // appears or the last one goes.
    return <AnimatePresence initial={false}>
                {activeFilterPills.length > 0 && <motion.div key="pills" className="overflow-hidden" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }}>
                        <div data-testid="activity-plp-filter-pills" className="flex flex-wrap items-center gap-2 pb-5">
                            {pillButtons}
                        </div>
                    </motion.div>}
            </AnimatePresence>;
  }
  return <div data-testid="activity-plp-filter-pills" className={cn('container flex flex-wrap items-center justify-center gap-2 md:mx-auto', props.hideOnDesktop && 'lg:hidden')}>
            {pillButtons}
        </div>;
};
const ActiveFilterPillButtons: React.FC<{
  pills: ReturnType<typeof buildActiveFilterPills>;
  inResultsHeader: boolean;
  backgroundType: 'light' | 'dark';
  onRemove: (pill: ReturnType<typeof buildActiveFilterPills>[number]) => void;
  onClear: () => void;
}> = props => {
  const $ = _c(10);
  const i18n = useI18n();
  const {
    inResultsHeader
  } = props;
  let t0;
  if ($[0] !== inResultsHeader || $[1] !== props) {
    t0 = props.pills.map(pill => <button type="button" key={`${pill.keys.join()}:${pill.label}`} onClick={() => props.onRemove(pill)} className={cn("flex select-none items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors", inResultsHeader && "border border-blue-100 bg-white text-blue hover:border-blue-300", !inResultsHeader && (props.backgroundType === "light" ? "bg-secondary-100 text-secondary hover:bg-secondary-200" : "border border-white/30 text-white hover:border-white hover:bg-white/10"))}>{pill.label}<X className={cn("size-3.5", inResultsHeader && "opacity-50")} /></button>);
    $[0] = inResultsHeader;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== i18n || $[4] !== inResultsHeader || $[5] !== props.onClear) {
    t1 = inResultsHeader && <button type="button" data-testid="activity-plp-clear-all" onClick={props.onClear} className="hit-area-2 px-1 text-sm font-medium text-tertiary transition-colors hover:text-tertiary-700">{i18n._("activity.plp.clearAll", undefined, {
        message: "Clear all"
      })}</button>;
    $[3] = i18n;
    $[4] = inResultsHeader;
    $[5] = props.onClear;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t0 || $[8] !== t1) {
    t2 = <>{t0}{t1}</>;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export declare namespace ActivityFilterBar {
  export type Props = {
    className?: ClassValue;
    wrapperClassName?: ClassNameValue;
    /** The styling of the filter bar has to be changed based on its background. */
    backgroundType: 'light' | 'dark';
    /** Smaller, denser pills for the merged search bar. */
    compact?: boolean;
    /** The sticky results header keeps the Filters / AI tabs in the sidebar. */
    hideModeToggle?: boolean;
    /** One-line summary pill, see `ActivityFiltersLayout.dense`. */
    dense?: boolean;
  } & Pick<React.ComponentProps<typeof ActivityFiltersLayout>, 'variant'>;
}
export const ActivityFilterBar: React.FC<ActivityFilterBar.Props> = props => {
  const posthog = usePostHog();
  const ctx = useActivitySearchCtx();
  const {
    setMode
  } = useActivitySearchMode();
  const aiEnabled = useAiSearchEnabled();
  const showDesktopFilterSidebar = (PostHog_ClientFeaturesSDK.getFlag(posthog, 'plp-filter-sidebar', 'control') ?? 'control') === 'sidebar';
  const state = ctx.useFilters('countryCode', 'categories', 'subCategories', 'participants', 'minBudget', 'maxBudget', 'nearLat', 'nearLng', 'nearLabel', 'radius', 'nearCleared', 'location', 'sort');
  const isFinetuning = usePlpExperiment();
  const filterBar = ACTIVITY_FILTER_BAR_PRESET.useFilters();
  const filters = useMemo(() => {
    if (ctx.hiddenFilters.length === 0) return filterBar;
    const hiddenKeys: readonly string[] = ctx.hiddenFilters;
    return filterBar.filter(x => !hiddenKeys.includes(x.key));
  }, [ctx.hiddenFilters, filterBar]);
  return <div className={cn('relative z-2 flex flex-col items-center gap-5', props.wrapperClassName)}>
            <ActivityFiltersLayout className={props.className} compact={props.compact} dense={props.dense} leading={aiEnabled && !props.hideModeToggle ? <SearchModeToggle mode="normal" onChange={() => setMode('ai')} theme="light" /> : undefined} filters={filters} mobileLayout={isFinetuning ? 'pair-outer' : 'default'}
    // The generic filter-config tuple `ActivityFiltersLayout` infers from its
    // `filters` prop does not line up with the state shape produced by
    // `ctx.useFilters(...)`; reconciling the two generic systems is a separate
    // behavior-risk refactor. The runtime values are correct.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic state/config shape mismatch, runtime-safe; tracked for a dedicated typing pass
    state={{
      value: state.value,
      set: state.update
    } as any} variant={props.variant} />

            {/* The reworked page shows the pills in the results header instead. */}
            {!isFinetuning && <ActiveFilterPills backgroundType={props.backgroundType} hideOnDesktop={showDesktopFilterSidebar} />}
        </div>;
};
const ActivityFilterBar_SubCategoryRow: React.FC<{
  backgroundType: ActivityFilterBar.Props['backgroundType'];
  className?: ClassValue;
}> = props => {
  const i18n = useI18n();
  const state = useActivitySearchCtx().useFilters('subCategories');
  const {
    subCategories,
    showSubCategoryRow
  } = useSubCategoryRow();
  // The reworked page shows a chosen category as a pill among the active
  // filters instead of a chip row of its own above the results.
  const isFinetuning = usePlpExperiment();
  if (!showSubCategoryRow || isFinetuning) return null;
  return <div className={cn('mb-4 flex w-full flex-wrap items-center justify-center gap-2 px-4 lg:mb-6 lg:px-0', props.className)}>
            <ActivityFilterBar_SubCat_ToggleButton backgroundType={props.backgroundType} pressed={state.value.subCategories.length === 0} onPressedChange={_pressed => state.update({
      subCategories: []
    })}>
                {i18n._('All')}
            </ActivityFilterBar_SubCat_ToggleButton>
            {subCategories?.map(cat => <ActivityFilterBar_SubCat_ToggleButton backgroundType={props.backgroundType} key={cat.id} pressed={state.value.subCategories.includes(cat.id)} onPressedChange={_pressed_0 => {
      state.update(prev => ({
        subCategories: std.Array.toggle(prev.subCategories, cat.id)
      }));
    }}>
                    {cat.name}
                </ActivityFilterBar_SubCat_ToggleButton>)}
        </div>;
};
const ActivityFilterBar_SubCat_ToggleButton: React.FC<Pick<React.ComponentProps<typeof Toggle>, 'pressed' | 'onPressedChange' | 'children'> & Pick<React.ComponentProps<typeof ActivityFilterBar>, 'backgroundType'>> = props => {
  const $ = _c(12);
  switch (props.backgroundType) {
    case "light":
      {
        let t0;
        if ($[0] !== props.children || $[1] !== props.onPressedChange || $[2] !== props.pressed) {
          t0 = <Toggle pressed={props.pressed} onPressedChange={props.onPressedChange} className="h-8 shrink-0 self-center whitespace-nowrap">{props.children}</Toggle>;
          $[0] = props.children;
          $[1] = props.onPressedChange;
          $[2] = props.pressed;
          $[3] = t0;
        } else {
          t0 = $[3];
        }
        return t0;
      }
    case "dark":
      {
        const t0 = props.pressed ? "border-secondary text-secondary" : "border-blue-900/15";
        let t1;
        if ($[4] !== t0) {
          t1 = cn("inline-flex h-8 shrink-0 items-center justify-center self-center whitespace-nowrap rounded-3xl border px-4 transition", "select-none bg-white text-sm font-medium leading-none text-blue-900/45 hover:border-secondary hover:text-secondary", t0);
          $[4] = t0;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        let t2;
        if ($[6] !== props) {
          t2 = () => props.onPressedChange?.(!props.pressed);
          $[6] = props;
          $[7] = t2;
        } else {
          t2 = $[7];
        }
        let t3;
        if ($[8] !== props.children || $[9] !== t1 || $[10] !== t2) {
          t3 = <button type="button" className={t1} onClick={t2}>{props.children}</button>;
          $[8] = props.children;
          $[9] = t1;
          $[10] = t2;
          $[11] = t3;
        } else {
          t3 = $[11];
        }
        return t3;
      }
    default:
      {
        return null;
      }
  }
};

/** `responsive` keeps the full "sort by X" control while the results header has
 * room for it and falls back to the icon button when it does not, so the count,
 * the filter summary and the sort stay on one line instead of wrapping. */
const ActivitySortButton: React.FC<{
  variant: 'full' | 'icon' | 'responsive';
}> = props => {
  const i18n = useI18n();
  const ctx = useActivitySearchCtx();
  const sort = ctx.useFilter('sort');
  const sortCenterLat = ctx.useFilter('sortCenterLat');
  const sortCenterLng = ctx.useFilter('sortCenterLng');
  const hasCenter = sortCenterLat.value != null && sortCenterLng.value != null;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const OPTIONS = useMemo(() => {
    // One icon per option, arrow and all: a euro going up or down, a
    // calendar going newest-first or oldest-first.
    const base: Array<{
      value: NonNullable<nuqs.inferParserType<typeof ACTIVITY_SORT_QUERY_PARAMS.sort>>;
      label: string;
      icon: IconName;
    }> = [{
      value: 'popularity.DESC',
      label: i18n._('Relevance'),
      icon: 'star'
    }, {
      value: 'price.ASC',
      label: i18n._('Price (Low to High)'),
      icon: 'price-up'
    }, {
      value: 'price.DESC',
      label: i18n._('Price (High to Low)'),
      icon: 'price-down'
    }, {
      value: 'createdAt.ASC',
      label: i18n._('Date (Oldest - Newest)'),
      icon: 'date-oldest'
    }, {
      value: 'createdAt.DESC',
      label: i18n._('Date (Newest - Oldest)'),
      icon: 'date-newest'
    }];
    // Distance option only appears when region city-search has set coords; without
    // coords the sort is meaningless and the resolver drops it anyway.
    if (hasCenter) {
      base.push({
        value: 'distance.ASC',
        label: i18n._('Distance (closest first)'),
        icon: 'location'
      });
    }
    return base;
  }, [i18n.locale, hasCenter]);
  return <>
            {/* Desktop sort */}
            {props.variant !== 'icon' && <InputSelect
    // Default sort is not in the URL; naming it here makes the control
    // show the chosen option instead of a placeholder.
    value={sort.value ?? 'popularity.DESC'} placeholder={i18n._('Relevance')} label={i18n._('Sort by')} labelPosition="left" labelClassName="font-normal text-blue-500" options={OPTIONS} onChange={newSort => sort.setValue(newSort as typeof sort.value)} iconBefore="sort"
    // The label keeps its own weight; the chosen option inherits this. A
    // fixed width keeps the row still when a longer option is picked.
    className={cn('hidden h-auto w-auto min-w-64 flex-initial font-bold', props.variant === 'responsive' ? '@5xl/results-header:flex' : 'sm:flex')} />}

            {/* Mobile sort */}
            <button type="button" className={cn(props.variant === 'full' ? 'relative block sm:hidden' : 'shadow-xs relative flex size-11 items-center justify-center rounded-full bg-white text-primary', props.variant === 'responsive' && '@5xl/results-header:hidden')} onClick={() => setIsOpen(true)}>
                <span className="sr-only">{i18n._('activity.overview.filter.cta')}</span>
                <Icon name="sort" />
            </button>

            <MobileSortSlideUp items={OPTIONS} value={sort.value} handleClick={newSort_0 => sort.setValue(newSort_0 as typeof sort.value)} onClose={() => setIsOpen(false)} open={isOpen} />
        </>;
};
function _temp(_, i) {
  return <div key={i} className="h-[334px]"><ActivityCard_Skeleton className="h-full" /></div>;
}